import {inject, Injectable, Type} from '@angular/core';
import {NotificationItemComponent} from './components/notification-item/notification-item.component';
import {Notification, NotificationApiResponse} from '../core/model/notification.model';
import {WebSocketService} from '../core/services/websocket.service';
import {catchError, concat, distinctUntilChanged, EMPTY, filter, from, map, merge, scan, Subject, switchMap} from 'rxjs';
import {takeUntilDestroyed, toSignal, toObservable} from '@angular/core/rxjs-interop';
import {Store} from '@ngxs/store';
import {ReadNotificationsAction} from './+state/actions';
import {getApiUrl} from '../core/helpers/env.helper';
import {HttpClient} from '@angular/common/http';
import {AuthService} from '../core/services/auth.service';

interface MessageDataPayload { type: string; data?: NotificationApiResponse; payload?: NotificationApiResponse }

@Injectable({
  providedIn: 'root'
})
export class NotificationService {
  private readonly store = inject(Store);
  private readonly httpClient = inject(HttpClient);
  private readonly authService = inject(AuthService);
  private readonly notificationComponentsMap: Record<string, Type<unknown>> = {
    defaultNotification: NotificationItemComponent,
  };

  private readonly accumulationHandler: (apiResponse: NotificationApiResponse) => (notifications: Notification[])
    => Notification[] = (apiResponse: NotificationApiResponse) => notifications => [
    ...notifications,
    {
      id: String(apiResponse.id),
      type: this.notificationComponentsMap[apiResponse.notificationType] ?? this.notificationComponentsMap['defaultNotification'],
      inputs: apiResponse.inputs,
    } satisfies Notification
  ];

  private readonly resetHandler: () => (notifications: Notification[])
    => Notification[] = () => (notifications: Notification[]) => {
    const ids = notifications.map(n => n.id);
    this.store.dispatch(new ReadNotificationsAction(ids));
    return [];
  };

  private readonly readNotificationHandler: (id: string) => (notifications: Notification[])
    => Notification[] = (id: string) => (notifications: Notification[]) => notifications.filter(n => n.id !== id);

  private readonly wsService = inject(WebSocketService);

  /**
   * Fetches previous notifications only when the user is authenticated.
   * This prevents unnecessary HTTP requests when the user is not logged in.
   */
  private readonly previousMessages$ = toObservable(this.authService.authenticated).pipe(
    distinctUntilChanged(),
    switchMap(isAuthenticated => {
      if (isAuthenticated) {
        return getApiUrl('notification').pipe(
          switchMap(url =>
            this.httpClient.get<{ data: NotificationApiResponse[] }>(`${url}/notifications`).pipe(
              map(response => response.data),
              catchError(() => EMPTY)
            ),
          ),
          switchMap(messages => from(messages)),
        );
      } else {
        return EMPTY; // Return empty observable when not authenticated
      }
    })
  )

  readonly messages$ = concat(
    this.previousMessages$,
    this.wsService.getMessagesOfType<MessageDataPayload>('notification')
  ).pipe(
    filter((data): data is MessageDataPayload => Boolean(data)),
    map(message => message.data || message.payload),
    filter(Boolean),
  );

  private readonly resetSubject = new Subject<void>();
  private readonly readNotificationSubject = new Subject<string>();


  private readonly notificationStream$ = merge(
    this.messages$.pipe(
      map(this.accumulationHandler)
    ),
    this.resetSubject.pipe(
      map(this.resetHandler)
    ),
    this.readNotificationSubject.pipe(
      map(this.readNotificationHandler)
    ),
  ).pipe(
    scan((notifications, notificationHandlerFn) =>
      notificationHandlerFn(notifications), [] as Notification[]),
    takeUntilDestroyed()
  );

  readonly notifications = toSignal(this.notificationStream$, {
    initialValue: []
  });


  /**
   * Resets all notification states by emitting a reset event.
   *
   * @return {void} Does not return a value.
   */
  resetNotifications(): void {
    this.resetSubject.next();
  }

  /**
   * Marks a notification as read by its identifier.
   *
   * @param {string} id - The unique identifier of the notification to mark as read.
   * @return {void} This method does not return a value.
   */
  readNotification(id: string): void {
    this.readNotificationSubject.next(id);
  }
}
